import { cn } from '@hbs-us/ui';
import type { ReactNode } from 'react';

/**
 * Generic token-backed status badge both `InvoiceStatusBadge` and
 * `EstimateStatusBadge` build on (UI-SPEC shared money components).
 *
 * The meaning is ALWAYS carried by the badge TEXT (the `children`), never color
 * alone (A11Y-01). The `tone` selects a locked semantic token mapping — there is
 * NO hardcoded hex; every variant resolves to `@hbs-us/ui` tokens (`--chart-3`
 * green, `--destructive` red, `--chart-4` amber, secondary/muted neutral) per
 * the UI-SPEC Color table.
 */

export type StatusTone = 'success' | 'destructive' | 'warning' | 'neutralBlue' | 'neutral';

const TONE_CLASSES: Record<StatusTone, string> = {
  success: 'border-(--chart-3)/30 bg-(--chart-3)/10 text-success',
  destructive: 'border-destructive/30 bg-destructive/10 text-destructive',
  warning: 'border-(--chart-4)/30 bg-(--chart-4)/10 text-warning',
  neutralBlue: 'border-transparent bg-secondary text-secondary-foreground',
  /*
   * `bg-muted` + `text-muted-foreground` measured 4.40:1 in light — under the
   * 4.5:1 AA floor for this 12px label. --muted-foreground is the kit's only
   * mid-grey text token, so the fix is the FILL, not the text: --background sits
   * one step off --card in both themes, taking the same grey to 4.63:1 light and
   * 7.72:1 dark. The border keeps it reading as a chip on a --background surface
   * where the fill matches, and keeps `neutral` visually distinct from the
   * filled `neutralBlue` pill (both fills would otherwise be --neutral-100).
   */
  neutral: 'border-border bg-background text-muted-foreground',
};

export function StatusBadge({
  tone,
  children,
  className,
}: {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md border px-2 py-0.5 font-medium text-xs',
        TONE_CLASSES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
